import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Divider, CircularProgress } from '@mui/material';
import { Description, Gavel, Assignment, Inventory } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { PublicRecordFormsComponent } from './publicFormComponent';
import { SummonsComponent } from "./summonsWarrantComponent";
import { WarrantComponent } from "./summonsWarrantComponent";
import { PetitionFormComponent } from './petitionComponent';
import { AssetManagementComponent } from './assetForms/assetFormMgmtComponent';
import { API_BASE_URL } from '../../../config';
import { ForensicAuditMatrixComponent } from './matrixPages/forensicAuditMatrixComponent';
import { CAActivityMatrixComponent } from './matrixPages/caActivityMatrix';

// ── Fetch helper ──────────────────────────────────────────────────────────────
const fetchPermission = async (url) => {
  try {
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        // attach auth token if needed:
        // Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!res.ok) return false;
    const data = await res.json();
    // At least one record must exist and be active + not deleted
    return Array.isArray(data) && data.some(
      (entry) => entry.is_active && !entry.is_deleted
    );
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export const FormsManagementComponent = () => {
  const role = useSelector((s) => s.auth.role);
  const [tab, setTab] = useState(0);

  // API-gated permission flags
  const [publicRecordAllowed, setPublicRecordAllowed] = useState(false);
  const [warrantAllowed, setWarrantAllowed]           = useState(false);
  const [permissionsLoading, setPermissionsLoading]   = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      setPermissionsLoading(true);
      const [pubAllowed, warAllowed] = await Promise.all([
        fetchPermission(`${API_BASE_URL}/fe-mgmt/public-record-case-worker/`),
        fetchPermission(`${API_BASE_URL}/fe-mgmt/warrant-case-worker/`),
      ]);
      setPublicRecordAllowed(pubAllowed);
      setWarrantAllowed(warAllowed);
      setPermissionsLoading(false);
    };

    loadPermissions();
  }, []);

  // ── Tab definitions ─────────────────────────────────────────────────────────
  // `apiAllowed`: if true, tab also requires the API permission flag to show.
  // `apiFlag`:    the permission boolean for that tab.
  const ALL_TABS = [
    {
      label: 'Public Record Forms',
      icon: <Description fontSize="small" />,
      roles: [
        'super_staff', 'shirastedar', 'tahsildar',
        'competent_authority', 'admin', 'sploca',
      ],
      apiAllowed: true,
      apiFlag: publicRecordAllowed,
      component: <PublicRecordFormsComponent />,
    },
    {
      label: 'Summons',
      icon: <Gavel fontSize="small" />,
      roles: [
        'staff', 'super_staff', 'shirastedar', 'legal',
        'assistant_commissioner', 'joint_secretary',
        'competent_authority', 'admin', 'sploca',
      ],
      apiAllowed: false,
      component: <SummonsComponent />,
    },
    {
      label: 'Warrant',
      icon: <Gavel fontSize="small" />,
      roles: [
        'staff', 'super_staff', 'shirastedar', 'legal',
        'assistant_commissioner', 'joint_secretary',
        'competent_authority', 'admin', 'sploca',
      ],
      apiAllowed: true,
      apiFlag: warrantAllowed,
      component: <WarrantComponent />,
    },
    {
      label: 'Petition',
      icon: <Assignment fontSize="small" />,
      roles: [
        'legal', 'assistant_commissioner', 'joint_secretary',
        'competent_authority', 'admin', 'sploca',
      ],
      apiAllowed: false,
      component: <PetitionFormComponent />,
    },
    {
      label: 'Asset Management',
      icon: <Inventory fontSize="small" />,
      roles: [
        'super_staff', 'shirastedar', 'tahsildar',
        'assistant_commissioner', 'joint_secretary',
        'competent_authority', 'admin', 'sploca',
      ],
      apiAllowed: false,
      component: <AssetManagementComponent />,
    },

    {
      label: 'Forensic Audit Matrix',
      icon: <Inventory fontSize="small" />,
      roles: [
        "forensic_auditor", "competent_authority", "sploca"
      ],
      apiAllowed: false,
      component: <ForensicAuditMatrixComponent />,
    },

    {
      label: 'CA Matrix',
      icon: <Inventory fontSize="small" />,
      roles: [
        "competent_authority", "sploca"
      ],
      apiAllowed: false,
      component: <CAActivityMatrixComponent />,
    },
  ];

  // Filter by role first, then by API permission if applicable
  const visible = ALL_TABS.filter((t) => {
    const hasRole = t.roles.includes(role);
    if (!hasRole) return false;
    if (t.apiAllowed) return t.apiFlag; // must also pass API gate
    return true;
  });

  const safeTab = Math.min(tab, visible.length - 1);

  // ── Loading state ────────────────────────────────────────────────────────────
  if (permissionsLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading permissions…</Typography>
      </Box>
    );
  }

  // ── No access ────────────────────────────────────────────────────────────────
  if (!visible.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          You do not have access to any forms in this module.
        </Typography>
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
        Forms Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Public Record Forms · Summons &amp; Warrant · Petitions
      </Typography>
      <Divider sx={{ mb: 0 }} />

      <Tabs
        value={safeTab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: '1px solid #e0e0e0',
          '& .MuiTab-root': { fontWeight: 600 },
        }}
      >
        {visible.map((t, i) => (
          <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      <Box>{visible[safeTab]?.component}</Box>
    </Box>
  );
};